import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LinkContainer } from "react-router-bootstrap";
import styled from "styled-components";

import { newOrderUrl, orderEditUrl } from "@ROM-utils/urlHelpers";
import NumberFormater from "@ROM-ui/NumberFormater";
import { getFilteredOrders, fetchDraftOrder } from "@ROM/Orders/actions";
import { selectLoading, selectOrders, selectOrdersPagination, selectOrdersTotal, selectOrdersNonInvoiced, selectDraftOrder } from "@ROM/Orders/selectors";
import { selectCurrentUser } from "@ROM/Auth/selectors";
import { selectRegions } from "@ROM/Regions/selectors";
import { selectPermanentWarehousesUnpaginated } from "@ROM/Warehouses/selectors";
import { selectProducts } from "@ROM/Products/selectors";
import { canSeeOrdersFromOthers } from "@ROM/Orders/permissions";
import { loadOptions as customerLoadOptions, fetchUsers as creatorsLoadOptions } from "@ROM/Customers/utils";
import { loadOptions as productsLoadOptions } from "@ROM/Products/utils";
import useDebounceEffect from "@ROM-hooks/useDebounceEffect";
import useQueryStringFilters from "@ROM-hooks/useQueryStringFilters";
import CustomersApi from "@ROM/Customers/api";
import ProductsApi from "@ROM/Products/api";
import Button from "@ROM-ui/Button";
import ContentHeader from "@ROM-components/common/ContentHeader";
import OrdersList from "@ROM/Orders/components/List";
import Filters from "@ROM/Orders/components/Filters";
import SavedViewsTabs from "@ROM/Orders/components/SavedViews";
import Pagination from "@ROM-components/common/Pagination";

const Index = () => {
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);
  const orders = useSelector(selectOrders);
  const ordersLoading = useSelector(selectLoading);
  const pagination = useSelector(selectOrdersPagination);
  const totalAmount = useSelector(selectOrdersTotal);
  const draftOrder = useSelector(selectDraftOrder);
  const regions = useSelector(selectRegions);
  const warehouses = useSelector(selectPermanentWarehousesUnpaginated);
  const products = useSelector(selectProducts);

  const initialFilters = {
    status: "all_active",
    invoiceNumber: null,
    poNumber: null,
    beginDate: null,
    endDate: null,
    onlyMyOrders: !canSeeOrdersFromOthers(currentUser),
    creator: null,
    customerId: null,
    // sort: "-createdAt",
    regionId: null,
    warehouseId: null,
    productId: null,
    page: 1,
  };

  const [filters, setFilter] = useQueryStringFilters(initialFilters, { setInitialFiltersToQueryString: true });
  const [customerInitialValue, setCustomerInitialValue] = useState({ value: null, label: "All Customers" });
  const [productInitialValue, setProductInitialValue] = useState({ value: null, label: "All Products" });

  const savedViewsStorageKey = `rom_orders_saved_views_v1_${currentUser ? currentUser.id : "guest"}`;

  const handleFilterChange = (name, value) => {
    // Filter changes return to 1st page, except when changing the page.
    setFilter({ page: 1, [name]: value });
  };

  const handleClearFilters = () => {
    setFilter(initialFilters);
  };

  const handlePageClick = (page) => {
    handleFilterChange("page", page);
  };

  const handleColumnSort = (field) => {
    const sortField = field;
    handleFilterChange("sort", `${filters.sort === sortField ? `-${sortField}` : sortField}`);
  };

  const orderCount = (quantity) => {
    if (quantity > 1) {
      return `${quantity} Total Orders`;
    }
    if (quantity === 1) {
      return "One Order";
    }
    return "No Orders";
  };

  useDebounceEffect(() => {
    dispatch(getFilteredOrders(filters));
    dispatch(fetchDraftOrder());
  }, [filters]);

  useEffect(() => {
    if (filters.customerId) {
      CustomersApi.find(filters.customerId)
        .then((customer) => {
          setCustomerInitialValue({
            value: filters.customerId,
            label: customer.data.data.attributes.name,
          });
        })
        .catch(() => {
          handleFilterChange("customerId", null);
        });
    } else {
      setCustomerInitialValue({ value: null, label: "All Customers" });
    }
  }, [filters.customerId]);

  useEffect(() => {
    if (filters.productId) {
      ProductsApi.find(filters.productId)
        .then((product) => {
          setProductInitialValue({
            value: filters.productId,
            label: product.data.data.attributes.name,
          });
        })
        .catch(() => {
          handleFilterChange("productId", null);
        });
    } else {
      setProductInitialValue({ value: null, label: "All Products" });
    }
  }, [filters.productId]);


  return (
    <>
      <ContentHeader>
        <ContentHeader.Left>
          <h3 className="fw-bolder text-white">My Orders</h3>
          <h5 className="fw-normal text-white">{orderCount(pagination.totalRecords)}</h5>
        </ContentHeader.Left>
        <ContentHeader.Right>
          <LinkContainer to={draftOrder ? orderEditUrl(draftOrder.id) : newOrderUrl()} className="float-end">
            <Button variant="outline-primary" className="px-5 bg-white text-primary btn" as="a" id="order-creation-btn">
              {draftOrder ? "Continue Order" : "Start a New Order"}
            </Button>
          </LinkContainer>
        </ContentHeader.Right>
      </ContentHeader>

      <SavedViewsTabs
        filters={filters}
        setFilter={setFilter}
        initialFilters={initialFilters}
        storageKey={savedViewsStorageKey}
      />

      <Filters
        filters={filters}
        handleFilterChange={handleFilterChange}
        handleClearFilters={handleClearFilters}
        currentUser={currentUser}
        creatorsLoadOptions={creatorsLoadOptions}
        customerInitialValue={customerInitialValue}
        regions={regions}
        warehouses={warehouses}
        productInitialValue={productInitialValue}
        productsLoadOptions={productsLoadOptions}
        customerLoadOptions={customerLoadOptions}
      />

      <ScrollableDiv className="shadow-sm p-2 bg-white rounded-2 mb-2">
        <OrdersList
          loading={ordersLoading}
          orders={orders}
          handleColumnSort={handleColumnSort}
          filters={filters}
          currentUser={currentUser}
        />
        <div className="d-flex justify-content-end align-items-center me-3">
          <p className="text-secondary"><strong>Active Orders Total:</strong> <NumberFormater value={totalAmount}/></p>
          <p className="text-secondary ms-3"><strong>To Be Invoiced:</strong> <NumberFormater value={useSelector(selectOrdersNonInvoiced)}/></p>
        </div>
      </ScrollableDiv>
      <Pagination pagination={pagination} handlePageClick={handlePageClick} align="center" />
    </>
  );
};

const ScrollableDiv = styled.div`
  overflow-x: auto;
  .row {
    flex-wrap: nowrap;
  }
  @media (min-width: 768px) {
    .row:not(:first-child) {
      background-color: #f8f9fa !important;
    }
  }
`;

export default Index;
